<template>
  <div class="dialog">
    <span class="cls" @click="oth_close()"></span>
    <div class="paper">
      <!-- 헤더 -->
      <div class="header">
        <h2>선물 내역</h2>
        <button class="close-btn" @click="oth_close()">
          <i class="ph-bold ph-x"></i>
        </button>
      </div>

      <!-- 탭 -->
      <div class="tabs">
        <button 
          :class="{ active: activeTab === 'owned' }" 
          @click="activeTab = 'owned'"
        >
          보유리스트
          <span v-if="totalOwnedCount > 0" class="tab-count">{{ totalOwnedCount }}</span>
        </button>
        <button 
          :class="{ active: activeTab === 'sent' }" 
          @click="activeTab = 'sent'"
        >
          보낸선물
          <span v-if="sortedSentGifts.length > 0" class="tab-count">{{ sortedSentGifts.length }}</span>
        </button>
        <button class="sort-btn" @click="toggleSort">
          <i :class="sortOrder === 'desc' ? 'ph-bold ph-sort-descending' : 'ph-bold ph-sort-ascending'"></i>
        </button>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="loading">
        <i class="ph-bold ph-spinner"></i>
        <span>불러오는 중...</span>
      </div>

      <!-- 선물 보유리스트 -->
      <div v-else-if="activeTab === 'owned'" class="content">
        <div v-if="sortedOwnedGifts.length === 0" class="empty">
          <i class="ph-bold ph-gift"></i>
          <span>보유 중인 선물이 없습니다</span>
        </div>
        <div v-else class="gift-grid">
          <div 
            v-for="(gift, index) in sortedOwnedGifts" 
            :key="`${gift.brand_name}_${gift.goods_name}_${index}`" 
            class="gift-card"
            @click="handleGiftClick(gift)"
          >
            <div class="gift-image">
              <img 
                v-if="gift.goods_img" 
                :src="gift.goods_img" 
                :alt="gift.goods_name"
              />
              <i v-else class="ph-fill ph-gift"></i>
            </div>
            <div class="gift-info">
              <span class="brand">{{ gift.brand_name }}</span>
              <p class="name">{{ gift.goods_name }}</p>
              <div class="quantity">
                남은 수량 <strong>{{ gift.count }}개</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 보낸선물 -->
      <div v-else class="content">
        <div v-if="sortedSentGifts.length === 0" class="empty">
          <i class="ph-bold ph-paper-plane"></i>
          <span>보낸 선물이 없습니다</span>
        </div>
        <div v-else class="sent-list">
          <div 
            v-for="gift in sortedSentGifts" 
            :key="gift.idx" 
            class="sent-item"
          >
            <div class="sent-image">
              <img 
                v-if="gift.goods_img" 
                :src="gift.goods_img" 
                :alt="gift.goods_name"
              />
              <i v-else class="ph-fill ph-gift"></i>
            </div>
            <div class="sent-info">
              <span class="brand">{{ gift.brand_name }}</span>
              <p class="name">{{ gift.goods_name }}</p>
              <div class="recipient">
                <i class="ph-bold ph-user"></i>
                <span>{{ gift.receiver_name || gift.recipient_name }}</span>
              </div>
              <div class="date">{{ formatDate(gift.issued_at) }}</div>
            </div>
            <div class="sent-actions">
              <div class="status-badge" :class="gift.status">
                {{ getStatusText(gift.status) }}
              </div>
              <button 
                v-if="gift.status === '제공'" 
                class="cancel-gift-btn"
                @click="handleCancelGift(gift)"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 선물 상세 다이얼로그 -->
    <giftDetailDialog 
      v-if="showGiftDetail && selectedGift" 
      :gift="selectedGift"
      :oth_close="handleDetailClose"
    />
  </div>
</template>

<script>
import { ref, inject, onMounted, computed } from 'vue';
import giftDetailDialog from './giftDetailDialog.vue';

export default {
  name: 'giftHistoryDialog',
  components: {
    giftDetailDialog
  },
  props: {
    oth_close: {
      type: Function,
      required: true
    }
  },
  setup() {
    const sql_query = inject('sql_query');
    const member_info = inject('member_info');

    const activeTab = ref('owned');
    const loading = ref(true);
    const ownedGifts = ref([]);
    const sentGifts = ref([]);
    const sortOrder = ref('desc'); // desc: 최신순, asc: 오래된순
    
    // 선물 상세 다이얼로그
    const showGiftDetail = ref(false);
    const selectedGift = ref(null);

    // 보유 중인 선물 조회
    async function fetchOwnedGifts() {
      try {
        const sql = `
          SELECT 
            idx,
            coupon_code,
            coupon_type as brand_name,
            memo,
            issued_at
          FROM giftcoupon
          WHERE issued_by = '${member_info.mb_id}'
            AND status IN ('발행', '사용대기')
          ORDER BY issued_at DESC
        `;
        const res = await sql_query(sql);
        
        console.log('📦 보유선물 조회 결과:', res);

        // 에러 체크
        if (res && res.error) {
          console.error('❌ 보유선물 SQL 에러:', res.error, res.details);
          ownedGifts.value = [];
          return;
        }

        if (!res || !Array.isArray(res) || res.length === 0) {
          ownedGifts.value = [];
          return;
        }

        // JavaScript에서 그룹핑
        const grouped = {};
        res.forEach(item => {
          try {
            const memoData = typeof item.memo === 'string' ? JSON.parse(item.memo) : (item.memo || {});
            const key = `${item.brand_name}_${memoData?.goods_name || 'unknown'}`;
            
            if (!grouped[key]) {
              grouped[key] = {
                brand_name: item.brand_name,
                goods_name: memoData?.goods_name || '상품명 없음',
                goods_img: memoData?.goods_img || '',
                brand_img: memoData?.brand_img || '',
                count: 0,
                latest_issued: item.issued_at
              };
            }
            grouped[key].count++;
          } catch (e) {
            console.error('memo 파싱 오류:', e, item);
            // 파싱 실패해도 카드는 보이도록
            const key = `${item.brand_name}_unknown_${item.idx}`;
            if (!grouped[key]) {
              grouped[key] = {
                brand_name: item.brand_name,
                goods_name: '상품명 없음',
                goods_img: '',
                brand_img: '',
                count: 0,
                latest_issued: item.issued_at
              };
            }
            grouped[key].count++;
          }
        });

        console.log('📦 그룹핑 결과:', grouped);

        ownedGifts.value = Object.values(grouped).sort((a, b) => 
          new Date(b.latest_issued) - new Date(a.latest_issued)
        );
      } catch (error) {
        console.error('보유 선물 조회 실패:', error);
        ownedGifts.value = [];
      }
    }

    // 보낸 선물 조회 (제공/사용완료/만료 상태)
    async function fetchSentGifts() {
      try {
        const sql = `
          SELECT 
            gc.idx,
            gc.coupon_code,
            gc.coupon_type as brand_name,
            gc.memo,
            gc.issued_to as recipient_name,
            gc.status,
            gc.issued_at,
            gc.ktgift_expire_date,
            gl.token as link_token,
            gl.status as link_status,
            gl.receiver_name,
            gl.receiver_phone
          FROM giftcoupon gc
          LEFT JOIN gift_links gl ON gc.idx = gl.coupon_idx 
            AND gl.idx = (
              SELECT MAX(gl2.idx) FROM gift_links gl2 WHERE gl2.coupon_idx = gc.idx
            )
          WHERE gc.issued_by = '${member_info.mb_id}'
            AND gc.status IN ('제공', '사용완료', '만료')
          ORDER BY gc.issued_at DESC
        `;
        const res = await sql_query(sql);
        
        console.log('📨 보낸선물 조회 결과:', res);

        if (res && res.error) {
          console.error('❌ 보낸선물 SQL 에러:', res.error, res.details);
          sentGifts.value = [];
          return;
        }

        if (!res || !Array.isArray(res) || res.length === 0) {
          sentGifts.value = [];
          return;
        }

        sentGifts.value = res.map(item => {
          try {
            const memoData = typeof item.memo === 'string' ? JSON.parse(item.memo) : item.memo;
            return {
              ...item,
              goods_name: memoData?.goods_name || '상품명 없음',
              goods_img: memoData?.goods_img || '',
              brand_img: memoData?.brand_img || ''
            };
          } catch (e) {
            return {
              ...item,
              goods_name: '상품명 없음',
              goods_img: '',
              brand_img: ''
            };
          }
        });
      } catch (error) {
        console.error('보낸 선물 조회 실패:', error);
        sentGifts.value = [];
      }
    }

    // 정렬된 보유 선물
    const sortedOwnedGifts = computed(() => {
      if (!Array.isArray(ownedGifts.value)) return [];
      return [...ownedGifts.value].sort((a, b) => {
        if (sortOrder.value === 'desc') {
          return new Date(b.latest_issued) - new Date(a.latest_issued);
        } else {
          return new Date(a.latest_issued) - new Date(b.latest_issued);
        }
      });
    });

    // 보유 쿠폰 총 개수 (그룹별 count 합산)
    const totalOwnedCount = computed(() => {
      return sortedOwnedGifts.value.reduce((sum, g) => sum + g.count, 0);
    });

    // 정렬된 보낸 선물
    const sortedSentGifts = computed(() => {
      if (!Array.isArray(sentGifts.value)) return [];
      return [...sentGifts.value].sort((a, b) => {
        if (sortOrder.value === 'desc') {
          return new Date(b.issued_at) - new Date(a.issued_at);
        } else {
          return new Date(a.issued_at) - new Date(b.issued_at);
        }
      });
    });

    // 정렬 토글
    function toggleSort() {
      sortOrder.value = sortOrder.value === 'desc' ? 'asc' : 'desc';
    }

    // 날짜 포맷
    function formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    }

    // 상태 텍스트
    function getStatusText(status) {
      const statusMap = {
        '발행': '보유중',
        '사용대기': '사용대기',
        '제공': '수령대기',
        '사용완료': '수령완료',
        '취소': '취소됨',
        '만료': '만료됨'
      };
      return statusMap[status] || status;
    }

    // 선물 카드 클릭
    function handleGiftClick(gift) {
      selectedGift.value = gift;
      showGiftDetail.value = true;
    }

    // 상세 다이얼로그 닫기 → 목록 새로고침
    async function handleDetailClose() {
      showGiftDetail.value = false;
      selectedGift.value = null;
      // 보내기/취소 했을 수 있으니 목록 갱신
      await Promise.all([fetchOwnedGifts(), fetchSentGifts()]);
    }

    // 🆕 보낸 선물 취소 (쿠폰 되돌림)
    async function handleCancelGift(gift) {
      const confirmed = confirm(
        `${gift.goods_name}\n받는 사람: ${gift.receiver_name || gift.recipient_name}\n\n선물을 취소하고 쿠폰을 되돌리시겠습니까?`
      );
      if (!confirmed) return;

      try {
        // 쿠폰 상태를 '발행'으로 되돌림
        await sql_query(`
          UPDATE giftcoupon 
          SET status = '발행', issued_to = NULL
          WHERE idx = ${gift.idx}
        `);

        // gift_links 상태 만료 처리
        if (gift.link_token) {
          await sql_query(`
            UPDATE gift_links 
            SET status = 'expired'
            WHERE token = '${gift.link_token}'
          `);
        }

        alert('선물이 취소되었습니다. 쿠폰이 되돌아왔습니다.');

        // 목록 새로고침
        await Promise.all([fetchOwnedGifts(), fetchSentGifts()]);
      } catch (e) {
        console.error('선물 취소 오류:', e);
        alert('취소 중 오류가 발생했습니다');
      }
    }

    onMounted(async () => {
      loading.value = true;
      try {
        await Promise.all([
          fetchOwnedGifts(),
          fetchSentGifts()
        ]);
      } catch (error) {
        console.error('선물 내역 로딩 실패:', error);
      } finally {
        loading.value = false;
      }
    });

    return {
      activeTab,
      loading,
      ownedGifts,
      sentGifts,
      sortedOwnedGifts,
      sortedSentGifts,
      totalOwnedCount,
      sortOrder,
      showGiftDetail,
      selectedGift,
      toggleSort,
      formatDate,
      getStatusText,
      handleGiftClick,
      handleDetailClose,
      handleCancelGift
    };
  }
}
</script>

<style scoped>
.dialog {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999999;
}

.cls {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(4px);
  z-index: -1;
}

.paper {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 520px;
  max-height: 85vh;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 헤더 */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
}

.header h2 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.close-btn {
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-lg);
  border-radius: var(--radius-full);
  transition: all 0.2s;
}

.close-btn:hover {
  background: var(--color-surface);
  color: var(--color-text-primary);
}

/* 탭 */
.tabs {
  display: flex;
  gap: 8px;
  padding: 12px 20px;
  border-bottom: 2px solid var(--color-border);
  flex-shrink: 0;
}

.tabs button {
  padding: 8px 16px;
  border: none;
  background: none;
  color: var(--color-text-tertiary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
}

.tabs button.active {
  color: var(--color-text-primary);
  font-weight: var(--font-weight-bold);
}

.tabs button.active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -12px;
  height: 2px;
  background: var(--color-primary);
}

.tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: var(--color-primary);
  color: #fff;
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  margin-left: 4px;
}

.tabs button.active .tab-count {
  background: var(--color-primary);
}

.sort-btn {
  margin-left: auto !important;
  padding: 6px 12px !important;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-md) !important;
  background: var(--color-surface) !important;
  color: var(--color-text-secondary) !important;
}

.sort-btn:hover {
  background: var(--color-surface-deep) !important;
}

/* 로딩 */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px;
  color: var(--color-text-secondary);
}

.loading i {
  font-size: 32px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 컨텐츠 */
.content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

/* 빈 상태 */
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: var(--color-text-tertiary);
}

.empty i {
  font-size: 48px;
  color: var(--color-text-quaternary);
}

/* 선물 그리드 (보유리스트) */
.gift-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
}

.gift-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.gift-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: var(--color-primary);
}

.gift-image {
  width: 80px;
  height: 80px;
  border-radius: var(--radius-md);
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.gift-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gift-image i {
  font-size: 40px;
  color: var(--color-text-quaternary);
}

.gift-info {
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.gift-info .brand {
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  font-weight: var(--font-weight-medium);
}

.gift-info .name {
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.gift-info .quantity {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  margin-top: 4px;
}

.gift-info .quantity strong {
  color: var(--color-brand);
  font-weight: var(--font-weight-bold);
}

/* 보낸 선물 리스트 */
.sent-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sent-item {
  display: flex;
  gap: 12px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 12px;
  transition: all 0.2s;
}

.sent-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.sent-image {
  width: 60px;
  height: 60px;
  border-radius: var(--radius-md);
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.sent-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sent-image i {
  font-size: 30px;
  color: var(--color-text-quaternary);
}

.sent-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sent-info .brand {
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  font-weight: var(--font-weight-medium);
}

.sent-info .name {
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  line-height: 1.3;
}

.sent-info .recipient {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.sent-info .recipient i {
  font-size: 12px;
}

.sent-info .date {
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
}

.status-badge {
  padding: 4px 10px;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  height: fit-content;
  align-self: center;
}

.status-badge.발행,
.status-badge.사용대기 {
  background: #e3f2fd;
  color: #1976d2;
}

.status-badge.제공 {
  background: #fff3e0;
  color: #e65100;
}

.status-badge.사용완료 {
  background: #e8f5e9;
  color: #388e3c;
}

.status-badge.취소,
.status-badge.만료 {
  background: #ffebee;
  color: #d32f2f;
}

/* 보낸선물 액션 영역 */
.sent-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  align-self: center;
  flex-shrink: 0;
}

.cancel-gift-btn {
  padding: 4px 10px;
  border: 1px solid #ef5350;
  border-radius: var(--radius-sm);
  background: none;
  color: #ef5350;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-gift-btn:hover {
  background: #ef5350;
  color: #fff;
}

/* 모바일 */
@media (max-width: 480px) {
  .paper {
    width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .gift-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .gift-card {
    padding: 12px;
  }

  .gift-image {
    width: 60px;
    height: 60px;
  }
}
</style>